<!--suppress HtmlFormInputWithoutLabel -->
<template>
  <div class="splash">
    <NavBar noPadding :login-status="!!communeSelected" />
    <SplashHeader
      :app-name="getAppName()"
      :commune-selected="communeSelected"
      :current-location="currentLocation"
      :accommodation-deactivated-error="accommodationDeactivatedError"
    />
    <div class="splash-content">
      <div class="firstactions" v-if="communeSelected">
        <Button
          class="firstactions-button"
          v-on:click="$router.push({ name: $namedRoutes.login })"
          color="green"
          center="center"
          path="login.submit.button"
          v-if="!loggedInUser"
        />
        <Button
          class="firstactions-button"
          @click.native="!!communeSelected && goToBooking()"
          center="center"
          path="splash.action.bookTaxcard.label"
        />
        <div class="cram">
          <div class="cram-button" :class="{ noCommune: !communeSelected }" v-on:click="onTransferClick()">
            <font-awesome-icon icon="plus" />
          </div>
          <input
            class="firstactions-input cram-input"
            type="text"
            v-model="taxId"
            :placeholder="translate('splash.transferTaxcard.input.placeholder')"
          />
        </div>
      </div>
      <router-link
        v-if="communeSelected"
        class="changelocation"
        :to="{ name: $namedRoutes.locationOptions, params: { communeId: $route.params.communeId } }"
      >
        <NewTranslation path="splash.action.changeLocation.label" />
      </router-link>
    </div>
    <SplashFooter :commune-selected="communeSelected" />
    <TabBar :no-commune-on-splash="!communeSelected" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { namespace } from 'vuex-class';
import Logo from '@/components/Logo.vue';
import Button from '@/components/Button.vue';
import TabBar from '@/components/TabBar.vue';
import NavBar from '@/components/NavBar.vue';
import AccommodationQRScanner from '@/components/qr/accommodation/AccommodationQRScanner';
import NewTranslation from '@/components/i18n/NewTranslation';
import i18next from 'i18next';
import Booking from '@/types/taxcard/Booking';
import BIObject from '@/types/taxcard/BIObject';
import { NamedRoutes } from '@/routes/routes';
import AuthRedirect from '@/app/utils/AuthRedirect';
import AccommodationDeepLink from '@/app/utils/AccommodationDeepLink';
import actions from '@/store/modules/booking/actions';
import AccommodationApi from '@/app/api/AccommodationApi';
import SplashHeader from './partials/SplashHeader';
import SplashFooter from './partials/SplashFooter';
import FullDeepLink from '@/app/utils/FullDeepLink';
import moment, { Moment } from 'moment';
import { GuestTypesEnum } from '@/types/taxcard/GuestType';
import BIObjectData from '@/types/taxcard/BIObjectData';

const I18nModule = namespace('i18n');

export default {
  components: {
    SplashFooter,
    SplashHeader,
    AccommodationQRScanner,
    Logo,
    TabBar,
    Button,
    NavBar,
    NewTranslation,
  },
  data: () => {
    return {
      taxId: '',
      accommodationDeactivatedError: false,
    };
  },
  created() {
    if (this.$route.query.transferCode) {
      this.taxId = this.$route.query.transferCode;
      this.initiateTransfer(this.$route.query.transferCode);
    }
    if (this.$route.query.addressStreetAndNumber) {
      this.initiateBookingWithAccommodation();
    }
  },
  methods: {
    ...mapMutations(['changeLocation']),
    ...mapMutations('booking', ['setCurrentBooking']),
    ...mapActions(['loadCommuneData']),
    translate(path, params) {
      return i18next.t(path, params);
    },
    getAppName() {
      return (this.communeInfo && this.communeInfo.appName) || 'WELCMpass';
    },
    onTransferClick() {
      const taxId = this.taxId;
      if (taxId.length === 0) {
        this.showEmptyTransferFieldError();
        return;
      }
      //if not logged in: request login
      if (Boolean(this.loggedInUser)) {
        this.initiateTransfer(taxId);
      } else {
        AuthRedirect.setRedirect({
          name: NamedRoutes.splash,
          query: { transferCode: taxId },
          params: { communeId: this.$route.params.communeId },
        });
        this.$router.push({
          name: NamedRoutes.login,
        });
      }
    },
    cleanupTaxId(taxId) {
      const taxIdParts = taxId.trim().split('-');
      let index = taxIdParts.reverse().shift();
      index = parseInt(index).toString(); // removes trailing 0
      const cardNumber = taxIdParts.reverse().join('-');
      this.taxId = [cardNumber, index].join('-');
    },
    initiateBookingWithAccommodation() {
      const url = new URL(location.href);
      const fullDeepLinkProjection = FullDeepLink.buildDeepLinkObject(url);
      if (!fullDeepLinkProjection) {
        this.accommodationDeactivatedError = true;
      }
      if (fullDeepLinkProjection) this.startBookingWithFullDeepLinkProjection(fullDeepLinkProjection);
    },
    initiateTransfer(taxId) {
      this.cleanupTaxId(taxId);
      this.$store.commit('openOverlay', {
        overlayName: 'EnterTransferCode',
        overlayData: {
          taxId: this.taxId,
        },
      });
      this.taxId = '';
    },
    showEmptyTransferFieldError() {
      this.showInfo(
        i18next.t('splash.transferTaxcard.dialog.error.validation.title'),
        i18next.t('api.taxcardTransferCommand.validation.taxId.isEmpty')
      );
    },
    showInfo(title, message, submit) {
      this.$store.commit('openOverlay', {
        overlayName: 'Info',
        overlayData: {
          title,
          message,
          submit: submit ? submit : () => {},
        },
      });
    },
    async startBookingWithFullDeepLinkProjection(fullDeepLinkProjection) {
      // create booking data and navigate to booking page
      const communeId = parseInt(fullDeepLinkProjection.communeId, 10);
      const booking = new Booking(communeId);
      booking.addBIObject(new BIObject(new BIObjectData()));
      const fetchAccommodation = await this.fetchAccommodation(fullDeepLinkProjection.id, communeId);
     const salutationElements = ['MR', 'MRS', 'OTHER'];
     let customSalutation = '';
     if (fullDeepLinkProjection.guestSalutation && fullDeepLinkProjection.guestSalutation >= 1 && fullDeepLinkProjection.guestSalutation <= 3) {
      customSalutation = salutationElements[fullDeepLinkProjection.guestSalutation-1];
     }

     let guestBirthdate;
     if (fullDeepLinkProjection.guestBirthdate) {
      guestBirthdate = moment(fullDeepLinkProjection.guestBirthdate, 'YYYY-MM-DD').utc();
      if (!guestBirthdate.isValid()) {
        guestBirthdate = null;
      }
     }

     let guestArrivalDate;
     if (fullDeepLinkProjection.guestArrivalDate) {
      guestArrivalDate = moment(fullDeepLinkProjection.guestArrivalDate, 'YYYY-MM-DD');
      if (!guestArrivalDate.isValid()) {
        guestArrivalDate = null;
      }
     }

     let guestDepartureDate;
     if (fullDeepLinkProjection.guestDepartureDate) {
      guestDepartureDate = moment(fullDeepLinkProjection.guestDepartureDate, 'YYYY-MM-DD');
      if (!guestDepartureDate.isValid()) {
        guestDepartureDate = null;
      }
     }     

     booking.bIObjects[0].bIObjectData.guestType = GuestTypesEnum.overnight;
     booking.bIObjects[0].bIObjectData.isAnonymous = false;
     booking.bIObjects[0].bIObjectData.accommodation = fetchAccommodation;
     booking.bIObjects[0].bIObjectData.salutation = customSalutation;
     booking.bIObjects[0].bIObjectData.firstName = fullDeepLinkProjection.guestFirstName ? fullDeepLinkProjection.guestFirstName : '';
     booking.bIObjects[0].bIObjectData.lastName = fullDeepLinkProjection.guestLastName ? fullDeepLinkProjection.guestLastName : '';
     booking.bIObjects[0].bIObjectData.birthday = guestBirthdate;
     booking.bIObjects[0].bIObjectData.stayBegin = guestArrivalDate;
     booking.bIObjects[0].bIObjectData.stayEnd = guestDepartureDate;
     booking.bIObjects[0].bIObjectData.addressStreetAndNumber = fullDeepLinkProjection.guestStreetAndNumber ? fullDeepLinkProjection.guestStreetAndNumber : '';
     booking.bIObjects[0].bIObjectData.addressCity = fullDeepLinkProjection.guestCity ? fullDeepLinkProjection.guestCity : '';
     booking.bIObjects[0].bIObjectData.addressZipCode = fullDeepLinkProjection.guestPostCode ? fullDeepLinkProjection.guestPostCode.toString() : '';
     booking.bIObjects[0].bIObjectData.email = fullDeepLinkProjection.guestEmail ? fullDeepLinkProjection.guestEmail : '';
     booking.bIObjects[0].bIObjectData.cellphoneNumber = fullDeepLinkProjection.guestCellphoneNumber ? fullDeepLinkProjection.guestCellphoneNumber : '';
     booking.bIObjects[0].bIObjectData.accommodationsProvidersBookingNo = fullDeepLinkProjection.guestAProvidersBookingNo ? fullDeepLinkProjection.guestAProvidersBookingNo : null;

      this.changeLocation(communeId);
      this.loadCommuneData().then(() => {
        booking.strictAccommodation = this.communeInfo.strictAccommodationSelection;
        this.setCurrentBooking(booking);
        this.$router.push({ name: NamedRoutes.booking, params: { communeId: communeId } });
      });
    },
    async fetchAccommodation(uid, communeId) {
      const result = await AccommodationApi.fetchAccommodation(communeId, uid);
      // return id here to differentiate between deleted and create new accommodation
      return result.id ? result : { id: uid };
    },
    goToBooking() {
      this.resetBooking();
      this.$router.push({ name: NamedRoutes.booking });
    },
    ...mapActions({ resetBooking: 'booking/resetBooking' }),
  },
  computed: {
    ...mapGetters(['currentLocation', 'communeInfo', 'loggedInUser']),
    communeSelected() {
      return this.currentLocation && this.currentLocation.id !== -1;
    },
  },
};
</script>

<style lang="scss" scoped>
.splash {
  background-image: url(../../assets/splash.png);
  background-position: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
  padding: 0 20px 80px;
  height: 100vh;
}
.splash-content {
  flex-grow: 1;
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.changelocation {
  display: inline-block;
  margin-top: 1em;
  text-decoration: underline;
  cursor: pointer;
}
</style>

<style lang="sass" scoped>
.firstactions
    width: 100%
    &-button
        margin-bottom: 1rem
    &-input
        width: 100%
        padding: 20px
        font-size: 1rem
        border-radius: 10px
    .cram
        margin-top: 1rem
        position: relative
        &-button
            background-color: #1db2d1
            position: absolute
            right: 0
            // border: 1px solid white
            width: 60px
            height: 100%
            border-radius: 0 10px 10px 0
            display: flex
            align-items: center
            justify-content: center
            svg
                max-height: 100%
                width: 15px
            &.noCommune
              pointer-events: none
              cursor: not-allowed
              background-color: rgba(239, 239, 239, 0.3)
              color: rgba(59, 59, 59, 0.3)
</style>
